import type { UnifiedEvent } from "@/lib/types";

const asString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const asNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const readStringAttr = (event: UnifiedEvent, keys: string[]): string | null => {
  for (const key of keys) {
    const value = asString(event.attributes[key]);
    if (value) return value;
  }
  return null;
};

const readNumberAttr = (event: UnifiedEvent, keys: string[]): number | null => {
  for (const key of keys) {
    const value = asNumber(event.attributes[key]);
    if (value !== null) return value;
  }
  return null;
};

export type EventDetails = {
  pluginMethod: string | null;
  nodeId: string | null;
  dbOperation: string | null;
  dbStatement: string | null;
  dbDatabase: string | null;
  dbRepo: string | null;
  dbParamsJson: string | null;
  dbRowCount: number | null;
};

export const extractEventDetails = (event: UnifiedEvent): EventDetails => ({
  pluginMethod: readStringAttr(event, ["w7s.plugin.method"]),
  nodeId: readStringAttr(event, ["w7s.node.id"]),
  dbOperation: readStringAttr(event, ["db.operation.name"]),
  dbStatement: readStringAttr(event, ["db.statement"]),
  dbDatabase: readStringAttr(event, ["w7s.db.database"]),
  dbRepo: readStringAttr(event, ["w7s.db.repo"]),
  dbParamsJson: readStringAttr(event, ["w7s.db.params_json"]),
  dbRowCount: readNumberAttr(event, ["w7s.db.result.rows"]),
});

export const compactSql = (value: string, max = 260): string => {
  const singleLine = value.replace(/\s+/g, " ").trim();
  if (singleLine.length <= max) return singleLine;
  return `${singleLine.slice(0, max)}…`;
};

export const buildEventSecondaryLine = (event: UnifiedEvent): string | null => {
  const details = extractEventDetails(event);
  if (details.dbStatement) {
    const base = `SQL ${details.dbOperation ?? "query"}: ${compactSql(details.dbStatement, 180)}`;
    return typeof details.dbRowCount === "number"
      ? `${base} · rows=${details.dbRowCount}`
      : base;
  }
  if (details.pluginMethod) {
    return `method: ${details.pluginMethod}`;
  }
  return null;
};
